import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axiosPrivate from 'config/axiosPrivate';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { useHistory } from 'react-router-dom';
import { createPortal } from 'react-dom';
import Tooltip from '@mui/material/Tooltip';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import './SidebarWebsiteDropdown.css';

export default function SidebarWebsiteDropdown({ isCollapsed = false, canAddWebsite = true, storageKey = 'AC_Dashboard_SelectedWebsite', skipCompanyQuotas = false }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedWebsiteUrl, setSelectedWebsiteUrl] = useWebsiteFilter(storageKey);
  const [websites, setWebsites] = useState([]);
  const [companyQuotas, setCompanyQuotas] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [selectedWebsiteLabel, setSelectedWebsiteLabel] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isWebsiteLimitModalOpen, setIsWebsiteLimitModalOpen] = useState(false);
  const cardRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    let mounted = true;
    const fetchWebsites = async () => {
      setIsLoading(true);
      try {
        const res = await axiosPrivate.get('/project/website/all');
        if (!mounted) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const normalized = list
          .filter(Boolean)
          .map((w) => ({
            id: w.id,
            urlSite: w.urlSite || w.url || w.website?.urlSite || w.name,
            name: w.name || w.websiteName || w.urlSite || w.url,
          }))
          .filter((w) => !!w.urlSite);
        setWebsites(normalized);
      } catch (e) {
        if (mounted) {
          setWebsites([]);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchWebsites();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (skipCompanyQuotas) return undefined;

    let mounted = true;
    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!mounted) return;
        setCompanyQuotas(data || null);
      } catch (e) {
        if (!mounted) return;
        setCompanyQuotas(null);
      }
    };

    fetchCompanyQuotas();
    return () => {
      mounted = false;
    };
  }, [skipCompanyQuotas]);

  const maxWebsites = Number(companyQuotas?.maxWebsites);
  const currentWebsites = Number(companyQuotas?.currentWebsites);
  const hasQuotaValues = !skipCompanyQuotas && Number.isFinite(maxWebsites) && Number.isFinite(currentWebsites);
  const canAddWebsiteByQuota = skipCompanyQuotas || !hasQuotaValues || (maxWebsites - currentWebsites > 0);
  const normalizedPlanKey = String(companyQuotas?.planKey || 'free').trim().toLowerCase();
  const isFreePlan = !skipCompanyQuotas && normalizedPlanKey === 'free';
  const showAddWebsiteButton = canAddWebsite;

  useEffect(() => {
    const toDomain = (u) =>
      (u || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();

    if (!selectedWebsiteUrl) {
      setFaviconUrl('');
      setSelectedWebsiteLabel('');
      setIsOpen(false);
      return;
    }

    const domain = toDomain(selectedWebsiteUrl);

    if (!domain) {
      setFaviconUrl('');
      setSelectedWebsiteLabel('');
      setIsOpen(false);
      return;
    }

    const url = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
      domain
    )}&sz=64`;
    setFaviconUrl(url);

    const current = websites.find(
      (w) => w.urlSite === selectedWebsiteUrl
    );
    const label =
      current?.name ||
      domain ||
      selectedWebsiteUrl;
    setSelectedWebsiteLabel(label);
  }, [selectedWebsiteUrl, websites]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;

      const dropdownMenu = document.getElementById('sidebar-website-dropdown-menu');
      const clickedInsideCard = cardRef.current && cardRef.current.contains(event.target);
      const clickedInsideDropdown = dropdownMenu && dropdownMenu.contains(event.target);

      if (!clickedInsideCard && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      id="sidebar-website-dropdown"
      style={{
        width: '100%',
        padding: '0',
        marginTop: '-4px',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
      }}
    >
      <div
        ref={cardRef}
        className="sidebar-website-card"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: isCollapsed ? '2px' : '4px 6px 2px 10px',
          borderRadius: '12px',
          backgroundColor: 'transparent',
          marginBottom: 0,
          cursor: isLoading || websites.length === 0 ? 'default' : 'pointer',
          width: isCollapsed ? 52 : '100%',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
        }}
        onClick={() => {
          if (isLoading || websites.length === 0) return;
          try {
            if (cardRef.current) {
              const rect = cardRef.current.getBoundingClientRect();
              const top = Math.max(8, rect.top);
              const left = rect.right + 8;
              setDropdownPosition({ top, left });
            }
          } catch (e) { }
          setIsOpen((v) => !v);
        }}
      >
        <div
          style={{
            width: isCollapsed ? 40 : 48,
            height: isCollapsed ? 40 : 48,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {faviconUrl ? (
            <img
              src={faviconUrl}
              alt=""
              style={{
                width: isCollapsed ? 28 : 34,
                height: isCollapsed ? 28 : 34,
                borderRadius: 6,
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#6366F1',
              }}
            >
              {selectedWebsiteLabel?.[0]?.toUpperCase() || 'W'}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0, display: isCollapsed ? 'none' : 'block' }}>
          <Tooltip
            title={selectedWebsiteUrl || t('Sélectionner un site pour afficher le détail')}
            placement="top"
            arrow
          >
            <div
              className="sidebar-website-main-label"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedWebsiteUrl
                ? selectedWebsiteUrl.replace(/^https?:\/\//i, '')
                : t('Sélectionner un site pour afficher le détail')}
            </div>
          </Tooltip>

          <Tooltip
            title={selectedWebsiteLabel || selectedWebsiteUrl || t('Aucun site sélectionné')}
            placement="top"
            arrow
          >
            <div
              className="sidebar-website-sub-label"
              style={{
                fontSize: 11,
                color: '#6B7280',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedWebsiteLabel || t('Aucun site sélectionné')}
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Liste déroulante custom */}
      {isOpen &&
        !isLoading &&
        websites.length > 0 &&
        createPortal(
          <div
            id="sidebar-website-dropdown-menu"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: 230,
              maxWidth: 260,
              borderRadius: 10,
              border: '1px solid #E5E7EB',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 340,
              zIndex: 99999,
            }}
          >
            {/* Liste scrollable des sites */}
            <div
              style={{
                overflowY: 'auto',
                flex: 1,
                padding: '6px 0',
              }}
            >
              {websites.map((site) => {
                const toDomain = (u) =>
                  (u || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();
                const domain = toDomain(site.urlSite);
                const fav =
                  domain
                    ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
                      domain
                    )}&sz=64`
                    : null;
                const isSelected = site.urlSite === selectedWebsiteUrl;
                return (
                  <div
                    key={site.id}
                    onClick={() => {
                      setSelectedWebsiteUrl(site.urlSite);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#EEF0FF' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {fav ? (
                        <img
                          src={fav}
                          alt=""
                          style={{ width: 32, height: 32, borderRadius: 6 }}
                        />
                      ) : (
                        <span
                          style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#6366F1',
                          }}
                        >
                          {(site.name || domain || 'W')?.[0]?.toUpperCase() || 'W'}
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Tooltip
                        title={site.name || domain || site.urlSite}
                        placement="top"
                        arrow
                        enterDelay={200}
                        PopperProps={{
                          style: { zIndex: 100000 }
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#111827',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {site.name || domain || site.urlSite}
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={site.urlSite}
                        placement="top"
                        arrow
                        enterDelay={200}
                        PopperProps={{
                          style: { zIndex: 100000 }
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: '#6B7280',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {site.urlSite.replace(/^https?:\/\//i, '')}
                        </div>
                      </Tooltip>
                    </div>
                    {isSelected && (
                      <span
                        style={{
                          fontSize: 14,
                          color: '#4F46E5',
                          fontWeight: 600,
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bouton fixe "Ajouter un site web" en bas (optionnel) */}
            {showAddWebsiteButton && (
              <div
                onClick={() => {
                  if (!canAddWebsiteByQuota) {
                    setIsOpen(false);
                    setIsWebsiteLimitModalOpen(true);
                    return;
                  }
                  try {
                    window.localStorage.removeItem('editWebsiteId');
                  } catch (e) { }
                  history.push('/AdminClient/AjouterSiteWeb');
                  setIsOpen(false);
                }}
                style={{
                  position: 'sticky',
                  bottom: 0,
                  padding: '10px 0 12px',
                  borderTop: '1px solid #E5E7EB',
                  cursor: 'pointer',
                  textAlign: 'center',
                  backgroundColor: '#FFFFFF',
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    color: '#111827',
                    backgroundColor: '#F3F3F1',
                    borderRadius: '10px',
                    padding: '10px 32px',
                    minWidth: 220,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      marginRight: 2,
                    }}
                  >
                    +
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {t('Add new website')}
                  </span>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
      <QuitProjectCreationModal
        isOpen={isWebsiteLimitModalOpen}
        onClose={() => setIsWebsiteLimitModalOpen(false)}
        variant="enrichment"
        showCloseButton={false}
        titleIcon="/Images/black-magic-icon.png"
        title={t('Limite de sites atteinte')}
        message={
          isFreePlan ? (
            <>
              {t('Vous avez atteint le nombre maximum de sites web autorisés.')}
              {' '}
              <strong style={{ color: 'black' }}>{t('Passez à une offre supérieure')}</strong>
              {' '}
              {t('pour')}
              <strong style={{ color: 'black' }}>{t(' ajouter de nouveaux sites et')}</strong>
              {' '}
              {t('étendre votre activité')}
              .
            </>
          ) : (
            <>
              {t('Vous avez atteint le nombre maximum de sites web autorisés.')}
              <br />
              <br />
              {t('Veuillez mettre à niveau votre offre actuelle pour ajouter un nouveau site.')}
            </>
          )
        }
        showSuggestion={false}
        primaryButtonLabel={isFreePlan ? t('Voir les offres') : t('Mettre à niveau')}
        secondaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => {
          setIsWebsiteLimitModalOpen(false);
          if (isFreePlan) {
            history.push('/AdminClient/Forfait');
            return;
          }
          history.push('/AdminClient/Abonnement?openManageSubscription=1');
        }}
        onSecondaryAction={() => setIsWebsiteLimitModalOpen(false)}
      />
    </div>
  );
}



